import React, { useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import ImageResize from 'tiptap-extension-resize-image';
import Link from "@tiptap/extension-link";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import FontFamily from "@tiptap/extension-font-family";
import { Node, Extension, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import { EditorToolbar, FontSize } from "./TipTapEditor";

// ─── Variable Node Extension (Protects {{1}} placeholders) ───────────────────


const tableBorderPluginKey = new PluginKey('tableBorderPlugin');
const TableBorderExtension = Extension.create({
    name: 'tableBorderExtension',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: tableBorderPluginKey,
                state: {
                    init() { return DecorationSet.empty; },
                    apply(tr, old, oldState, newState) {
                        const decorations = [];
                        newState.doc.descendants((node, pos) => {
                            if (node.type.name === 'table' && node.attrs.borderless) {
                                decorations.push(Decoration.node(pos, pos + node.nodeSize, { class: 'borderless-table-wrapper' }));
                            }
                        });
                        return DecorationSet.create(newState.doc, decorations);
                    }
                },
                props: {
                    decorations(state) {
                        return this.getState(state);
                    }
                }
            })
        ];
    }
});

const CustomTable = Table.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            borderless: {
                default: false,
                parseHTML: element => element.getAttribute('data-borderless') === 'true',
                renderHTML: attributes => {
                    if (attributes.borderless) {
                        return {
                            'data-borderless': 'true',
                            class: 'borderless-table',
                        };
                    }
                    return {};
                },
            },
        };
    },
});

const VariableNode = Node.create({
    name: "variable",
    group: "inline",
    inline: true,
    atom: true, // Prevents splitting, deleting parts of it, or typing inside
    selectable: false, // Prevents independent selection

    addAttributes() {
        return {
            id: { default: null },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-variable]',
                getAttrs: dom => ({
                    id: dom.getAttribute('data-variable'),
                })
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            "span",
            mergeAttributes(HTMLAttributes, {
                "data-variable": HTMLAttributes.id,
                class: "variable-chip",
            }),
            `{{${HTMLAttributes.id}}}`,
        ];
    },

    addKeyboardShortcuts() {
        return {
            Backspace: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from, empty } = selection;

                if (empty) {
                    const nodeBefore = $from.nodeBefore;
                    if (nodeBefore && nodeBefore.type.name === 'variable') {
                        return true; // Block backspace
                    }
                } else {
                    let containsVariable = false;
                    state.doc.nodesBetween(selection.from, selection.to, (node) => {
                        if (node.type.name === 'variable') containsVariable = true;
                    });
                    if (containsVariable) return true; // Block deletion of range containing variable
                }
                return false;
            },
            Delete: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from, empty } = selection;

                if (empty) {
                    const nodeAfter = $from.nodeAfter;
                    if (nodeAfter && nodeAfter.type.name === 'variable') {
                        return true; // Block delete
                    }
                } else {
                    let containsVariable = false;
                    state.doc.nodesBetween(selection.from, selection.to, (node) => {
                        if (node.type.name === 'variable') containsVariable = true;
                    });
                    if (containsVariable) return true;
                }
                return false;
            }
        };
    }
});

// ─── Pre/Post Processing ────────────────────────────────────────────────────
// Converts plain {{1}} from backend into <span data-variable="1">
const preprocessHTML = (html) => {
    if (!html) return "";
    let clean = html.replace(/<span[^>]*data-variable="(\d+)"[^>]*>{{(\d+)}}<\/span>/g, "{{$1}}");
    return clean.replace(/{{\d+}}/g, (match) => {
        const id = match.replace(/[{}]/g, "");
        return `<span data-variable="${id}" class="variable-chip">${match}</span>`;
    });
};

// Converts <span data-variable="1"> back to plain {{1}} for backend saving
// and injects inline styles for borderless tables to survive backend sanitization
const postprocessHTML = (html) => {
    if (!html) return "";

    // 1. Process variables
    let processed = html.replace(/<span[^>]*data-variable="(\d+)"[^>]*>{{(\d+)}}<\/span>/g, "{{$1}}");

    // 2. Inject inline styles for borderless tables so Preview works even if classes are stripped
    // if (processed.includes('data-borderless="true"')) {
    //     try {
    //         const parser = new DOMParser();
    //         const doc = parser.parseFromString(processed, 'text/html');
    //         const borderlessTables = doc.querySelectorAll('table[data-borderless="true"]');
    //         borderlessTables.forEach(table => {
    //             const cells = table.querySelectorAll('td, th');
    //             cells.forEach(cell => {
    //                 cell.style.setProperty('border', '0px', 'important');
    //             });
    //         });
    //         processed = doc.body.innerHTML;
    //     } catch (e) {
    //         console.error("Error processing table styles:", e);
    //     }
    // }
    // 2. Inject inline styles so Preview works even if classes are stripped
    if (processed.includes('<table')) {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(processed, 'text/html');

            doc.querySelectorAll('table').forEach(table => {
                // borderless (tumhara existing logic)
                if (table.getAttribute('data-borderless') === 'true') {
                    table.querySelectorAll('td, th').forEach(cell => {
                        cell.style.setProperty('border', '0px', 'important');
                    });
                }

                // NEW: har row ke pehle cell ka left aur aakhri cell ka right padding hatao
                table.querySelectorAll('tr').forEach(row => {
                    const cells = row.querySelectorAll(':scope > td, :scope > th');
                    if (!cells.length) return;
                    cells[0].style.setProperty('padding-left', '0', 'important');
                    cells[cells.length - 1].style.setProperty('padding-right', '0', 'important');
                });
            });

            processed = doc.body.innerHTML;
        } catch (e) {
            console.error("Error processing table styles:", e);
        }
    }
    return processed;
};

// ─── Main Component ─────────────────────────────────────────────────────────
const TipTapLetterEditor = forwardRef(({
    value = "",
    onChange,
    onFocus,
    onBlur,
    className = "",
    style = {},
    contentClassName = "",
}, ref) => {
    const isInternalUpdate = useRef(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3, 4, 5, 6] },
            }),
            TextAlign.configure({
                types: ["heading", "paragraph"],
                alignments: ["left", "center", "right", "justify"],
            }),
            Underline,
            TextStyle,
            Color,
            FontFamily,
            FontSize,
            Highlight.configure({ multicolor: true }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
            }),
            ImageResize.configure({ allowBase64: true, inline: true }),
            Subscript,
            Superscript,
            CustomTable.configure({ resizable: true, HTMLAttributes: { class: "letter-editor-table" } }),
            TableBorderExtension,
            TableRow,
            TableCell,
            TableHeader,
            VariableNode, // Custom node
        ],
        content: preprocessHTML(value),
        editorProps: {
            attributes: {
                class: "tiptap",
            },

            transformPastedHTML(html) {
                if (!html) return html;
                try {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');
                    doc.querySelectorAll('*').forEach(el => {
                        el.style.removeProperty('color');
                        el.style.removeProperty('background-color');
                        el.style.removeProperty('background');
                    });
                    return doc.body.innerHTML;
                } catch (e) {
                    return html;
                }
            }

        },
        onUpdate: ({ editor: editorInstance }) => {
            if (isInternalUpdate.current) return;
            const html = editorInstance.getHTML();
            console.log("TipTapLetterEditor Output", html);
            if (onChange) onChange(postprocessHTML(html));
        },
        onFocus: () => {
            if (onFocus) onFocus();
        },
        onBlur: () => {
            if (onBlur) onBlur();
        },

    });

    // Sync external value changes
    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        const currentHTML = postprocessHTML(editor.getHTML());
        if (value !== currentHTML && value !== undefined) {
            isInternalUpdate.current = true;
            editor.commands.setContent(preprocessHTML(value || ""), false);
            isInternalUpdate.current = false;
        }
    }, [value, editor]);

    // Expose methods
    useImperativeHandle(ref, () => ({
        getHTML: () => postprocessHTML(editor?.getHTML() || ""),
        setHTML: (html) => {
            if (!editor || editor.isDestroyed) return;
            isInternalUpdate.current = true;
            editor.commands.setContent(preprocessHTML(html || ""), false);
            isInternalUpdate.current = false;
        },
        getText: () => editor?.getText() || "",
        getEditor: () => editor,
        focus: () => editor?.chain().focus().run(),
        clear: () => {
            if (!editor || editor.isDestroyed) return;
            isInternalUpdate.current = true;
            editor.commands.setContent("", false);
            isInternalUpdate.current = false;
        },
        // Dedicated variable inserter
        insertVariable: (key) => {
            if (!editor) return;
            editor.chain().focus().insertContent([
                { type: "variable", attrs: { id: key } },
                { type: "text", text: " " },
            ]).run();
        },
    }), [editor]);

    return (
        <div className={`tiptap-editor-wrapper ${className}`} style={style}>
            <EditorToolbar editor={editor} />
            <div className={`tiptap-editor-content ${contentClassName}`}>
                <EditorContent editor={editor} />
            </div>
        </div>
    );
});

TipTapLetterEditor.displayName = "TipTapLetterEditor";

export default TipTapLetterEditor;







